import {inject, Injectable, OnDestroy} from '@angular/core';
import {API_URL} from '@scalified-monorepo/ng-shared';
import {ErrorService} from '@scalified-monorepo/ng-shared/errors';
import {Subject, BehaviorSubject, timer, takeUntil, Subscription} from 'rxjs';
import {SseConfig, DEFAULT_SSE_CONFIG} from './sse-config.interface';
import {SSE_CONFIG} from './sse-config.token';

export enum ConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

@Injectable({providedIn: 'root'})
export class SseService implements OnDestroy {
  private _apiUrl = inject(API_URL);
  private _errorService = inject(ErrorService, {optional: true});
  private _config: Required<SseConfig>;
  private subject = new Subject<unknown>();
  private connectionState$ = new BehaviorSubject<ConnectionState>(ConnectionState.DISCONNECTED);
  private destroy$ = new Subject<void>();

  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: Subscription | null = null;
  private heartbeatTimer: Subscription | null = null;
  private lastMessageTime = 0;

  /**
   * Set to true when an unauthorized error is detected.
   * Pauses the normal retry loop until forceReconnect() is called.
   */
  private waitingForTokenRefresh = false;

  /**
   * Emits once whenever the connection fails with an unauthorized error.
   * `SseEffects` subscribes to this stream and dispatches `UnauthorizedError` to the
   * NgRx store, which triggers the token-refresh flow. After a successful refresh,
   * `SseEffects` calls `forceReconnect()` to resume the connection.
   */
  private _unauthorizedError$ = new Subject<void>();
  readonly unauthorizedError$ = this._unauthorizedError$.asObservable();

  constructor() {
    const injectedConfig = inject(SSE_CONFIG, {optional: true});
    this._config = {...DEFAULT_SSE_CONFIG, ...injectedConfig};
  }

  connect() {
    // Reuse an existing open connection
    if (this.eventSource && this.eventSource.readyState === EventSource.OPEN) {
      console.log('Reusing existing SSE connection');
      return this;
    }

    this.clearReconnectTimer();
    this.establishConnection();
    return this;
  }

  private establishConnection() {
    // Guard against concurrent connection attempts by checking the actual
    // EventSource instance rather than state. This prevents a race where
    // connect() sets state=CONNECTING, forceReconnect() calls cleanup()
    // (closing the EventSource), then establishConnection() sees state
    // still CONNECTING and bails out — leaving no active connection.
    if (
      this.eventSource &&
      (this.eventSource.readyState === EventSource.CONNECTING || this.eventSource.readyState === EventSource.OPEN)
    ) {
      return;
    }

    const isReconnect = this.reconnectAttempts > 0;
    this.connectionState$.next(isReconnect ? ConnectionState.RECONNECTING : ConnectionState.CONNECTING);

    console.log(isReconnect ? 'Reconnecting to SSE' : 'Connecting to SSE');

    const url = `${this._apiUrl}/sse/register`;

    this.openEventSource(url);
  }

  private openEventSource(url: string) {
    if (this.connectionState$.value === ConnectionState.DISCONNECTED) {
      console.log('Skipping EventSource creation — disconnect() was already called');
      return;
    }
    this.eventSource = EventSource ? new EventSource(url, {withCredentials: true}) : null;
    if (!this.eventSource) {
      this.handleConnectionError('EventSource not supported');
      return;
    }

    this.eventSource.onopen = () => {
      console.log('SSE connection established');
      this.connectionState$.next(ConnectionState.CONNECTED);
      if (this.reconnectAttempts > 0) {
        this._errorService?.clearRetrying();
      }
      this.reconnectAttempts = 0;
      this.lastMessageTime = Date.now();
      this.startHeartbeatMonitoring();
    };

    this.eventSource.onmessage = (event: MessageEvent) => {
      this.lastMessageTime = Date.now();
      this.subject.next(event);
    };

    this.eventSource.onerror = (event: Event) => {
      console.error('SSE error', event);
      // EventSource does not expose HTTP status codes. Whenever an error fires,
      // assume the server rejected the connection (401 — expired session, or
      // revoked) and trigger token refresh instead of retrying forever.
      this.handleConnectionError('Connection error occurred', true);
    };
  }

  private handleConnectionError(reason: string, fromEventSourceError = false) {
    if (this.connectionState$.value === ConnectionState.DISCONNECTED) {
      console.log('Ignoring connection error — disconnect() was already called');
      return;
    }
    console.warn(`SSE connection failed: ${reason}`);
    this.connectionState$.next(ConnectionState.ERROR);
    this.cleanup();

    // EventSource.onerror gives us no HTTP status code. Whenever it fires,
    // the most likely cause is a 401 (expired session, corrupted, or
    // revoked). Trigger token refresh instead of entering an endless
    // retry loop with the same invalid session.
    if (fromEventSourceError) {
      console.warn('SSE: EventSource error fired — assuming 401, triggering token refresh');
      this.waitingForTokenRefresh = true;
      this._unauthorizedError$.next();
      return;
    }

    // Do not schedule a retry while waiting for a token refresh — the retry
    // will be initiated externally via forceReconnect() once the refresh completes.
    if (this.waitingForTokenRefresh) {
      return;
    }

    if (this.reconnectAttempts < this._config.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      console.error('Max reconnection attempts reached. Giving up.');
      this._errorService?.clearRetrying();
      this.connectionState$.next(ConnectionState.DISCONNECTED);
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();

    // Exponential backoff with jitter
    const delay = Math.min(
      this._config.baseReconnectDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000,
      this._config.maxReconnectDelay
    );

    this.reconnectAttempts++;
    this._errorService?.emitRetrying(this.reconnectAttempts, this._config.maxReconnectAttempts);
    console.log(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = timer(delay)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.establishConnection();
      });
  }

  private startHeartbeatMonitoring() {
    this.clearHeartbeatTimer();

    this.heartbeatTimer = timer(this._config.heartbeatInterval, this._config.heartbeatInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const timeSinceLastMessage = Date.now() - this.lastMessageTime;

        if (timeSinceLastMessage > this._config.heartbeatInterval * 2) {
          console.warn('No messages received for too long, reconnecting');
          this.handleConnectionError('Heartbeat timeout', false);
        }
      });
  }

  private cleanup() {
    if (this.eventSource) {
      // Detach handlers before close() so that any pending async callbacks
      // from this EventSource (especially onerror) are ignored. Without this,
      // an old EventSource's onerror can fire after forceReconnect() has
      // already opened a new one, causing handleConnectionError to close
      // the new EventSource and enter an infinite token-refresh loop.
      this.eventSource.onopen = null;
      this.eventSource.onmessage = null;
      this.eventSource.onerror = null;
      this.eventSource.close();
      this.eventSource = null;
    }
    this.clearHeartbeatTimer();
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      this.reconnectTimer.unsubscribe();
      this.reconnectTimer = null;
    }
  }

  private clearHeartbeatTimer() {
    if (this.heartbeatTimer) {
      this.heartbeatTimer.unsubscribe();
      this.heartbeatTimer = null;
    }
  }

  disconnect() {
    console.log('Manually disconnecting SSE');
    this.connectionState$.next(ConnectionState.DISCONNECTED);
    this.clearReconnectTimer();
    this.cleanup();
    this.reconnectAttempts = 0;
    this.waitingForTokenRefresh = false;
  }

  /**
   * Forces a reconnection, resetting all retry state.
   * Called by SseEffects after a successful token refresh to resume the connection.
   *
   * Delegates to connect() which already handles concurrent connection detection.
   */
  forceReconnect() {
    this.reconnectAttempts = 0;
    this.waitingForTokenRefresh = false;
    this.connect();
  }

  get events$() {
    return this.subject.asObservable();
  }

  get connectionState() {
    return this.connectionState$.asObservable();
  }

  get isConnected(): boolean {
    return this.connectionState$.value === ConnectionState.CONNECTED;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
